import type { FC, ReactNode } from 'react'
import type { TileLook } from '@/components/landing/mockups/media-tile'

import { Cast, Wifi } from 'lucide-react'
import MediaTile from '@/components/landing/mockups/media-tile'
import PhoneFrame from '@/components/landing/mockups/phone-frame'
import { TEAL_TONE, photos } from '@/constants/media'

const TV_TILES: TileLook[] = [
    { src: photos.sportsCar, position: '50% 58%' },
    { src: photos.surf, position: '62% 48%', filter: TEAL_TONE },
    { src: photos.breakdance, position: '50% 40%' },
    { src: photos.waterfall, position: '40% 45%' }
]

// A living room: the TV shows four views sent from the phone in front of it.
const TvScene: FC = (): ReactNode => {
    return (
        <div className='@container relative aspect-[16/11] w-full overflow-hidden rounded-2xl border border-border bg-[linear-gradient(180deg,#e7edf7_0%,#f3f6fb_80%,#d9e0ea_80%,#e5eaf1_100%)] dark:bg-[linear-gradient(180deg,#0e1526_0%,#121b30_80%,#0a101d_80%,#0c1322_100%)]'>
            <span className='absolute left-1/2 top-[34%] h-[52%] w-[82%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-[radial-gradient(closest-side,rgb(59_130_246/0.35),rgb(139_92_246/0.14)_60%,transparent)] blur-[2cqw]' />

            <div className='absolute left-1/2 top-[6%] w-[66%] -translate-x-1/2'>
                <div className='rounded-[1.2cqw] bg-[#0a0a0c] p-[0.7cqw] shadow-[0_2cqw_4cqw_-1cqw_rgb(2_6_23/0.55)] ring-1 ring-black/60'>
                    <div className='relative grid aspect-video grid-cols-2 grid-rows-2 gap-[0.35cqw] overflow-hidden rounded-[0.5cqw] bg-black'>
                        {TV_TILES.map((tile, index) => (
                            <div key={tile.src} className='relative overflow-hidden'>
                                <MediaTile {...tile} motion delay={index * 2.3} />
                            </div>
                        ))}

                        <span className='absolute left-[1.4cqw] top-[1.2cqw] z-10 inline-flex items-center gap-[0.6cqw] rounded-full bg-black/60 px-[1.2cqw] py-[0.5cqw] text-[1.2cqw] font-semibold leading-none text-white ring-1 ring-white/15'>
                            <span className='size-[0.9cqw] rounded-full bg-emerald-400' />
                            MultiScreen TV · 4 views
                        </span>
                    </div>
                </div>
            </div>

            <div className='absolute bottom-[13%] left-[19%] right-[19%] h-[11%] rounded-[0.9cqw] bg-[linear-gradient(180deg,#bf8b5c,#8a5a35)] shadow-[0_1.4cqw_2cqw_-1cqw_rgb(0_0_0/0.45)] dark:bg-[linear-gradient(180deg,#5b4030,#3b281c)]'>
                <span className='absolute inset-x-[4%] top-1/2 h-px bg-black/20' />
                <span className='absolute left-1/2 top-[22%] h-[56%] w-px bg-black/20' />
            </div>

            <span className='absolute bottom-[24%] left-1/2 h-[2.4%] w-[26%] -translate-x-1/2 rounded-full bg-zinc-800 shadow-sm' />

            <svg viewBox='0 0 60 100' aria-hidden='true' className='absolute bottom-[5%] left-[5%] w-[9%]'>
                <path d='M30 72 C29 50 20 36 8 26 C22 30 31 46 31 66 Z' fill='#16a34a' />
                <path d='M30 72 C32 48 42 32 54 24 C42 34 34 50 32 70 Z' fill='#22c55e' />
                <path d='M30 72 C27 56 24 44 30 10 C35 40 34 56 32 72 Z' fill='#15803d' />
                <path d='M30 72 C22 62 12 58 2 58 C14 54 24 58 31 70 Z' fill='#4ade80' />
                <path d='M30 72 C40 62 50 60 58 62 C48 56 38 60 31 70 Z' fill='#4ade80' />
                <path d='M17 70 H43 L40 98 H20 Z' fill='#d6c2a8' />
            </svg>

            <span className='absolute bottom-[4%] left-[17%] z-10 inline-flex items-center gap-[0.8cqw] rounded-full bg-card/90 px-[1.6cqw] py-[0.8cqw] text-[1.4cqw] font-semibold leading-none text-foreground shadow-[0_0.8cqw_2cqw_-0.8cqw_rgb(2_6_23/0.4)] ring-1 ring-border'>
                <Wifi className='size-[1.6cqw] text-brand' />
                Same local network
            </span>

            <div className='absolute -bottom-[14%] right-[7%] w-[18%] rotate-[-9deg]'>
                <PhoneFrame orientation='portrait' reflect={false}>
                    <div className='absolute inset-0 flex flex-col bg-[linear-gradient(180deg,#0d1424,#05070d)] px-[6cqw] pb-[10cqw] pt-[16cqw] text-white'>
                        <p className='text-[6.4cqw] font-bold leading-tight'>
                            Living room TV
                        </p>

                        <p className='mt-[1.6cqw] flex items-center gap-[2cqw] text-[4.6cqw] text-emerald-300'>
                            <span className='size-[2.6cqw] rounded-full bg-emerald-400' />
                            Connected
                        </p>

                        <div className='mt-[5cqw] grid aspect-video grid-cols-2 grid-rows-2 gap-[1.2cqw] overflow-hidden rounded-[3cqw]'>
                            {TV_TILES.map((tile) => (
                                <div key={tile.src} className='relative overflow-hidden'>
                                    <MediaTile {...tile} />
                                </div>
                            ))}
                        </div>

                        <span className='mt-[6cqw] flex items-center justify-center gap-[2.4cqw] rounded-full bg-primary py-[4cqw] text-[5cqw] font-semibold text-white'>
                            <Cast className='size-[6cqw]' />
                            Send to TV
                        </span>
                    </div>
                </PhoneFrame>
            </div>
        </div>
    )
}

export default TvScene
